import {NarrowPage} from "../../layout/Pages";
import {React} from "react";
import {Link} from "react-router-dom";
import {useAuth} from "../../../contexts/AuthContext";
import {getTenants} from "../../../api/accounts";
import {useQuery} from "@tanstack/react-query";
import {PrimaryButton} from "../../elements/Buttons";
import {preprocessAndIngestOwnDocuments} from "../../../api/documents";
import {getGoogleServiceAccounts, processOwnAsanaTasks, processOwnGoogleDriveFolders} from "../../../api/data_sources";
import {toast} from "react-toastify";

export default function HowItWorks() {

    const {currentUser} = useAuth()
    const {isLoading, isError, isSuccess, data, error, refetch} = useQuery(
        ["google_service_accounts", currentUser?.id],
        () => getGoogleServiceAccounts(),
        {refetchInterval: 1000 * 60 * 60, enabled: !!currentUser}
    )
    const {data: tenants} = useQuery(['tenants', currentUser?.id], getTenants, {refetchInterval: 1000 * 60 * 60 * 24})

    async function handleProcessGoogleDriveFolders() {
        try {
            await processOwnGoogleDriveFolders()
            toast.success('Processing started.')
        } catch (e) {
            toast.error('Processing failed.')
        }
    }

    async function handleProcessAsanaTasks() {
        try {
            await processOwnAsanaTasks()
            toast.success('Asana import started.')
        } catch (e) {
            toast.error('Asana import failed.')
        }
    }

    async function handlePreprocessAndIngestOwnDocuments() {
        try {
            await preprocessAndIngestOwnDocuments()
            toast.success('Processing started.')
        } catch (e) {
            toast.error('Processing failed.')
        }
    }

    return (

        <NarrowPage title={"How It Works"}>

            <div className="min-h-80 sm:grid sm:grid-cols-2 sm:gap-4 py-4">

                <div className="sm:col-span-2 mt-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Email</h2>

                    <ol className="list-decimal list-inside space-y-4">
                        <li>Email processing is
                            currently {tenants?.[0].email_inbound_active ? "enabled" : "disabled"}.
                        </li>
                        <li>Forward emails to {tenants?.[0].email_inbound_address}</li>
                        <li>They will be processed periodically.</li>
                    </ol>
                </div>

                <div className="sm:col-span-2 mt-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Google Drive</h2>

                    <ol className="list-decimal list-inside space-y-4">
                        <li>Add the Google Drive folder ID to your <Link
                            className="text-indigo-600 hover:text-indigo-900" to="/clients">client configuration</Link>.
                        </li>
                        <li>Share the Google Drive folder with your service account
                            email: {data?.filter(account => account.is_active).map(account => account.username).join(", ")}</li>
                        <li>Start the initial processing of the folders by clicking: <PrimaryButton
                            label="Process Folders" onClick={handleProcessGoogleDriveFolders}/> <br/>(please only do
                            this once & be patient, this can take a few minutes)
                        </li>
                        <li>Future changes to files in the folder will be automatically processed.</li>
                    </ol>
                </div>

                <div className="sm:col-span-2 mt-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Asana</h2>

                    <ol className="list-decimal list-inside space-y-4">
                        <li>Add the Asana project to your <Link className="text-indigo-600 hover:text-indigo-900"
                                                                to="/clients">client configuration</Link>.
                        </li>
                        <li>Wait or start the initial import by clicking: <PrimaryButton label="Process Asana Tasks"
                                                                                         onClick={handleProcessAsanaTasks}/>
                            <br/>(please only do this once & be patient, this can take a few minutes)
                        </li>
                        <li>Changes to tasks in Asana will be automatically processed periodically.</li>
                    </ol>
                </div>

                <div className="sm:col-span-2 mt-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Documents</h2>

                    <ol className="list-decimal list-inside space-y-4">
                        <li>You can upload documents directly <Link className="text-indigo-600 hover:text-indigo-900"
                                                                    to="/documemnts">here</Link>.
                        </li>
                        <li>Documents will be processed periodically. <PrimaryButton label="Process now"
                                                                                     onClick={handlePreprocessAndIngestOwnDocuments}/>
                            <br/>(please only do this once & be patient, this can take a few minutes)
                        </li>
                    </ol>
                </div>
            </div>
        </NarrowPage>
    )
}
